import React from 'react';
import { Database, Clock, Activity, HardDrive, RefreshCw, AlertCircle } from 'lucide-react';
import { useDatabaseStats, useSlowQueries, useIndexUsage } from '../hooks';

const DbMonitor: React.FC = () => {
  const { data: dbStats, isLoading: statsLoading, refetch: refetchStats } = useDatabaseStats();
  const { data: slowQueries, isLoading: queriesLoading } = useSlowQueries(10);
  const { data: indexUsage, isLoading: indexLoading } = useIndexUsage();

  const isLoading = statsLoading || queriesLoading || indexLoading;

  const handleRefresh = () => {
    refetchStats();
  };

  // Calculate connection pool percentages
  const connections = dbStats?.connections;
  const maxConnections = connections?.max_connections || 100;
  const activePercent = connections ? (connections.active_connections / maxConnections) * 100 : 0;
  const idlePercent = connections ? (connections.idle_connections / maxConnections) * 100 : 0;

  // Get longest query time from slow queries
  const longestQueryTime =
    slowQueries && slowQueries.length > 0 ? Math.max(...slowQueries.map((q) => q.mean_time_ms || 0)) : 0;

  // Count total rows from tables
  const totalRows = dbStats?.tables?.reduce((sum, t) => sum + (t.row_count || 0), 0) || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-10 h-10 mx-auto mb-4"></div>
          <p className="text-slate-500">Loading database stats...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-800">Database Monitoring</h2>
        <button
          onClick={handleRefresh}
          className="flex items-center gap-2 text-sm bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
        >
          <RefreshCw size={14} />
          Refresh (30s)
        </button>
      </div>

      {/* Connection Pool Visual */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-slate-800">Connection Pool</h3>
          <span className="text-xs font-mono text-slate-500">
            {connections?.total_connections || 0} / {maxConnections} Connections
          </span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden flex">
          <div
            className="bg-green-500 h-full transition-all duration-500"
            style={{ width: `${activePercent}%` }}
            title="Active"
          ></div>
          <div
            className="bg-blue-400 h-full transition-all duration-500"
            style={{ width: `${idlePercent}%` }}
            title="Idle"
          ></div>
        </div>
        <div className="flex gap-4 mt-3 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            Active ({connections?.active_connections || 0})
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-blue-400"></div>
            Idle ({connections?.idle_connections || 0})
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-slate-300"></div>
            Available ({maxConnections - (connections?.total_connections || 0)})
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          {
            label: 'Cache Hit Ratio',
            value: dbStats?.cache_hit_ratio != null ? `${dbStats.cache_hit_ratio}%` : '-',
            icon: Activity,
            color:
              dbStats?.cache_hit_ratio != null && dbStats.cache_hit_ratio >= 95 ? 'text-green-600' : 'text-orange-600',
            bg: dbStats?.cache_hit_ratio != null && dbStats.cache_hit_ratio >= 95 ? 'bg-green-50' : 'bg-orange-50',
          },
          {
            label: 'DB Size',
            value: dbStats?.database_size || '-',
            icon: HardDrive,
            color: 'text-purple-600',
            bg: 'bg-purple-50',
          },
          {
            label: 'Total Rows',
            value: totalRows.toLocaleString(),
            icon: Database,
            color: 'text-orange-600',
            bg: 'bg-orange-50',
          },
          {
            label: 'Longest Query',
            value: longestQueryTime > 0 ? `${Math.round(longestQueryTime)}ms` : '-',
            icon: Clock,
            color: 'text-blue-600',
            bg: 'bg-blue-50',
          },
        ].map((item, i) => (
          <div key={i} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-4">
            <div className={`p-3 rounded-lg ${item.bg} ${item.color}`}>
              <item.icon size={20} />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-800">{item.value}</div>
              <div className="text-xs text-slate-500">{item.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table Stats */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-800">Table Statistics</h3>
        </div>
        {dbStats?.tables && dbStats.tables.length > 0 ? (
          <table className="w-full text-left">
            <thead className="bg-white border-b border-slate-100">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Table Name</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Rows</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Total Size</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Index Size</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dbStats.tables.map((table, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-6 py-3 font-mono text-xs text-slate-600">{table.table_name}</td>
                  <td className="px-6 py-3 text-sm text-right text-slate-600">{table.row_count.toLocaleString()}</td>
                  <td className="px-6 py-3 text-sm text-right text-slate-600">{table.total_size}</td>
                  <td className="px-6 py-3 text-sm text-right text-slate-600">{table.index_size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-slate-500">
            <Database size={32} className="mx-auto mb-2 text-slate-300" />
            <p>No table data available</p>
          </div>
        )}
      </div>

      {/* Slow Queries Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h3 className="font-semibold text-slate-800">Slow Queries (&gt;100ms)</h3>
        </div>
        {slowQueries && slowQueries.length > 0 ? (
          <table className="w-full text-left">
            <thead className="bg-white border-b border-slate-100">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Query</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Calls</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Avg Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {slowQueries.map((query, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-6 py-3 font-mono text-xs text-slate-600 truncate max-w-md" title={query.query}>
                    {query.query?.substring(0, 80)}...
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-slate-600">{query.calls?.toLocaleString() || 0}</td>
                  <td
                    className={`px-6 py-3 text-sm text-right font-medium ${
                      (query.mean_time_ms || 0) > 500
                        ? 'text-red-600'
                        : (query.mean_time_ms || 0) > 100
                          ? 'text-orange-600'
                          : 'text-slate-600'
                    }`}
                  >
                    {Math.round(query.mean_time_ms || 0)}ms
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-slate-500">
            <AlertCircle size={32} className="mx-auto mb-2 text-slate-300" />
            <p>No slow queries detected</p>
            <p className="text-xs mt-1">pg_stat_statements extension may be required</p>
          </div>
        )}
      </div>

      {/* Index Usage */}
      {indexUsage && indexUsage.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h3 className="font-semibold text-slate-800">Index Usage</h3>
          </div>
          <table className="w-full text-left">
            <thead className="bg-white border-b border-slate-100">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Index Name</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Table</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Scans</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Size</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {indexUsage.map((idx, i) => (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-6 py-3 font-mono text-xs text-slate-600">{idx.index_name}</td>
                  <td className="px-6 py-3 text-sm text-slate-600">{idx.table_name}</td>
                  <td className="px-6 py-3 text-sm text-right text-slate-600">
                    {idx.index_scans?.toLocaleString() || 0}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-slate-600">{idx.index_size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DbMonitor;
